import { useEffect, useMemo, useState } from "react";
import { fetchBuilderSegments, fetchFeatureUsage, fetchResearchSummary, type FeatureUsageDto, type ResearchLibraryRowDto, type ResearchSummaryDto, type SegmentOptionDto, type TargetSpecDto } from "../api";
import {
  ApiError,
  blessFeature,
  fetchLabCandidates,
  fetchLabControls,
  investigateFeature,
  type CandidateRowDto,
  type ControlResultDto,
  type InvestigationResultDto,
} from "../api";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";

const WINDOWS = ["1y", "3y", "full"] as const;

function VerdictBadge({ verdict }: { verdict: string }) {
  const cls = verdict === "BLESS" ? "st-champion" : verdict === "MARGINAL" ? "chip" : "st-challenger";
  return <span className={`chip ${cls}`}>{verdict}</span>;
}

function Histogram({ bins }: { bins: { bin_lo: number; bin_hi: number; count: number }[] }) {
  if (!bins.length) return <div className="mut">no data</div>;
  const w = 400, h = 120, pad = 4;
  const max = Math.max(...bins.map((b) => b.count), 1);
  const bw = (w - 2 * pad) / bins.length;
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      {bins.map((b, i) => {
        const barH = (b.count / max) * (h - 2 * pad);
        return <rect key={i} x={pad + i * bw} y={h - pad - barH} width={Math.max(1, bw - 1)} height={barH} fill="var(--accent)" opacity={0.55} />;
      })}
    </svg>
  );
}

function RollingIcChart({ values }: { values: number[] }) {
  if (values.length < 2) return <div className="mut">not enough sub-periods</div>;
  const w = 400, h = 100, pad = 6;
  const min = Math.min(...values, 0), max = Math.max(...values, 0);
  const span = max - min || 1;
  const X = (i: number) => pad + (i / (values.length - 1)) * (w - 2 * pad);
  const Y = (v: number) => h - pad - ((v - min) / span) * (h - 2 * pad);
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <line x1={pad} y1={Y(0)} x2={w - pad} y2={Y(0)} stroke="var(--ink-faint)" strokeDasharray="3 3" />
      <polyline points={values.map((v, i) => `${X(i).toFixed(1)},${Y(v).toFixed(1)}`).join(" ")} fill="none" stroke="var(--accent)" strokeWidth={2} />
      {values.map((v, i) => <circle key={i} cx={X(i)} cy={Y(v)} r={3} fill={v >= 0 ? "var(--ok)" : "var(--bad)"} />)}
    </svg>
  );
}

function DecileChart({ means }: { means: number[] }) {
  if (!means.length) return <div className="mut">not enough paired observations</div>;
  const w = 400, h = 100, pad = 6;
  const max = Math.max(...means.map(Math.abs), 1e-6);
  const bw = (w - 2 * pad) / means.length;
  const zeroY = h / 2;
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <line x1={pad} y1={zeroY} x2={w - pad} y2={zeroY} stroke="var(--rule-strong)" />
      {means.map((v, i) => {
        const barH = (Math.abs(v) / max) * (h / 2 - pad);
        const y = v >= 0 ? zeroY - barH : zeroY;
        return <rect key={i} x={pad + i * bw} y={y} width={Math.max(1, bw - 2)} height={barH} fill={v >= 0 ? "var(--ok)" : "var(--bad)"} />;
      })}
    </svg>
  );
}

export function FeatureLabPage({
  token, onUnauthorized, onSelectModel, initialFeatureKey,
}: {
  token: string;
  onUnauthorized: () => void;
  onSelectModel: (modelId: string, version: string) => void;
  initialFeatureKey?: string;
}) {
  const [candidates, setCandidates] = useState<CandidateRowDto[] | null>(null);
  const [controls, setControls] = useState<ControlResultDto[] | null>(null);
  const [featureKey, setFeatureKey] = useState("");
  const [summary, setSummary] = useState<ResearchSummaryDto | null>(null);
  const [libFilter, setLibFilter] = useState<"all" | "blessed" | "not_blessed">("all");
  const [libSearch, setLibSearch] = useState("");
  const [libKind, setLibKind] = useState("all");
  const [picked, setPicked] = useState<ResearchLibraryRowDto | null>(null);
  const [usage, setUsage] = useState<FeatureUsageDto | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [pickNote, setPickNote] = useState<string | null>(null);
  const [targetType, setTargetType] = useState<"instrument" | "stocks_pooled" | "segment">("stocks_pooled");
  const [segmentKey, setSegmentKey] = useState("");
  const [segments, setSegments] = useState<SegmentOptionDto[]>([]);

  function targetSpec(): TargetSpecDto {
    if (targetType === "instrument") return { type: "instrument", security_id: securityId };
    if (targetType === "segment") return { type: "segment", segment_key: segmentKey };
    return { type: "stocks_pooled" };
  }

  useEffect(() => {
    fetchBuilderSegments(token).then(setSegments).catch(() => setSegments([]));
  }, [token]);
  const [securityId, setSecurityId] = useState("");
  const [horizon, setHorizon] = useState("5");
  const [window_, setWindow] = useState<(typeof WINDOWS)[number]>("1y");
  const [result, setResult] = useState<InvestigationResultDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [blessing, setBlessing] = useState(false);
  const [blessMsg, setBlessMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchResearchSummary(token).then(setSummary).catch(() => setSummary(null));
  }, [token]);

  useEffect(() => {
    if (!initialFeatureKey || !summary) return;
    const row = summary.library.rows.find((r) => r.feature === initialFeatureKey);
    if (row) pickFeature(row);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFeatureKey, summary]);

  useEffect(() => {
    if (!featureKey) return;
    setUsage(null);
    setUsageError(null);
    fetchFeatureUsage(token, featureKey).then(setUsage).catch((e) => setUsageError(String(e)));
  }, [featureKey, token]);

  useEffect(() => {
    if (picked || !summary || !candidates || initialFeatureKey) return;
    const offered = new Set(candidates.map((c) => c.feature_key));
    const top = summary.library.rows
      .filter((r) => r.blessed && offered.has(r.feature))
      .sort((a, b) => Math.abs(b.ic ?? 0) - Math.abs(a.ic ?? 0))[0];
    if (top) {
      setPicked(top);
      setFeatureKey(top.feature);
    }
  }, [summary, candidates, picked]);

  function pickFeature(r: ResearchLibraryRowDto) {
    setPicked(r);
    const inCandidates = (candidates ?? []).some((c) => c.feature_key === r.feature);
    if (inCandidates) {
      setFeatureKey(r.feature);
      setPickNote(null);
    } else {
      setPickNote(`${r.label ?? r.feature} is not offered by this investigation's target. Its sweep result is ${r.best_target ? `against ${r.best_target}` : "not recorded"}; the investigation below still shows the last feature you ran.`);
    }
  }

  function handleAuthAwareError(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      onUnauthorized();
      return true;
    }
    return false;
  }

  function loadCandidates() {
    fetchLabCandidates(token)
      .then((rows) => {
        setCandidates(rows);
        if (!featureKey && rows.length) setFeatureKey(rows[0].feature_key);
      })
      .catch((err) => {
        if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "request failed");
      });
  }

  useEffect(() => {
    loadCandidates();
    fetchLabControls(token).then(setControls).catch((err) => {
      if (!handleAuthAwareError(err)) console.error(err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function runInvestigation() {
    setLoading(true);
    setError(null);
    setBlessMsg(null);
    try {
      const res = await investigateFeature(token, {
        feature_key: featureKey,
        target: targetSpec(),
        horizon: Number(horizon),
        window: window_,
      });
      setResult(res);
    } catch (err) {
      if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? `${err.status}: ${err.message}` : "request failed");
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  async function doBless() {
    setBlessing(true);
    setBlessMsg(null);
    try {
      const res = await blessFeature(token, {
        feature_key: featureKey,
        target: targetSpec(),
        horizon: Number(horizon),
        window: window_,
      });
      setBlessMsg(`✓ ${res.feature_key} blessed (${res.verdict}) → feature_library`);
      loadCandidates();
    } catch (err) {
      if (!handleAuthAwareError(err)) setBlessMsg(`✕ ${err instanceof ApiError ? err.message : "bless failed"}`);
    } finally {
      setBlessing(false);
    }
  }

  const selectedCandidate = useMemo(
    () => candidates?.find((c) => c.feature_key === featureKey) ?? null,
    [candidates, featureKey],
  );


  const canRun = featureKey && (targetType === "stocks_pooled" || (targetType === "segment" ? segmentKey !== "" : securityId.trim().length > 0));

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Feature Lab</h1>
          <div className="page-sub">Interrogate a signal on its own merits — know your data, test significance, see it fitted — before it ever enters a model.</div>
        </div>
      </div>

      <PageHelper>
        Pick a stored technical/risk feature, choose what to test it against (one stock, or the whole pooled
        universe), and run it through the same real pipeline that decides whether a feature is trustworthy enough
        to <b>bless</b> — meaning admissible (not proven predictive; see <FieldHelp term="blessed" />), never a
        claim of strength. This bench never touches any model — it only decides what's ALLOWED into one later.
      </PageHelper>

      <div className="gaps-badge" style={{ display: "inline-flex", marginBottom: 16 }} title="This bench knows nothing about any model or the model registry. A feature's worth is established here, once.">
        ◆ Independent research — no link to any model
      </div>

      <div className="banner show" style={{ display: "flex" }}>
        <span>ⓘ</span>
        <span>
          Scope, stated: this build investigates <code>kind IN (tech, risk)</code> features (the active, growing
          case) against a single instrument or the full pooled stock cross-section. Macro features and the
          provisional-upload flow (F4a.7 — template download, alignment report) are not built here. A pooled
          investigation at the <code>full</code> window can take up to ~60s (a real, bounded query over the whole
          universe's history) — still one synchronous request, just slower; everything else is under ~1s.
        </span>
      </div>

      <div className="sechead" style={{ fontFamily: "var(--font-display)", fontWeight: 800, margin: "20px 0 10px" }}>
        SWEEP <span className="note">every hypothesis, tested once · selectivity is in the test funnels</span>
      </div>
      <div className="card" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
        {[
          ["Stock-family tests", summary?.stock ? `${summary.stock.tests} → ${summary.stock.fdr_relevant}` : "—", "clear FDR"],
          ["New data (macro · commodity · FPI)", summary?.phase2 ? `${summary.phase2.tests} → ${summary.phase2.lead_relevant}` : "—", "lead-relevant out of sample"],
          ["Incremental candidates", summary?.distinct_effects ? `${summary.distinct_effects.incremental_tests} → ${summary.distinct_effects.distinct_adds_value}` : "—", "distinct after de-redundancy"],
          ["Blessing gate", summary?.blessing_gate ? `${summary.blessing_gate.pairs} → ${summary.blessing_gate.ship}` : "—", "pairs that ship over current models"],
          ["Feature library", summary ? `${summary.library.total} · ${summary.library.blessed} blessed` : "—", "curated features"],
        ].map(([k, v, d]) => (
          <div key={k} className="stt">
            <div className="k">{k}</div>
            <div className="v">{v}</div>
            <div className="note">{d}</div>
          </div>
        ))}
      </div>
      <div className="card" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
        <div className="stt"><div className="k">Stock family · relevant after BH-FDR</div><div className="v">{summary?.stock ? `${summary.stock.fdr_relevant} / ${summary.stock.tests}` : "—"}</div></div>
        <div className="stt"><div className="k">Stock-family placebos cleared</div><div className="v">{summary?.stock ? `${summary.stock.placebo_relevant} / ${summary.stock.placebo_tests}` : "—"}</div></div>
        <div className="stt"><div className="k">New-data placebo · white noise</div><div className="v">{summary?.phase2 ? `${(summary.phase2.placebo_white_noise * 100).toFixed(1)}%` : "—"}</div></div>
        <div className="stt"><div className="k">New-data placebo · random walk</div><div className="v">{summary?.phase2 ? `${(summary.phase2.placebo_random_walk * 100).toFixed(1)}%` : "—"}</div></div>
      </div>

      {summary && (
        <div className="card">
          <div className="note" style={{ textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Library coverage · blessed vs not blessed</div>
          <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", border: "1px solid var(--line2)" }}>
            <div style={{ width: `${(summary.library.blessed / Math.max(1, summary.library.total)) * 100}%`, background: "#0e9f6e" }} />
            <div style={{ width: `${(summary.library.not_blessed / Math.max(1, summary.library.total)) * 100}%`, background: "#c4cbdb" }} />
          </div>
          <div style={{ display: "flex", gap: 16, fontSize: "0.76rem", color: "var(--ink-soft)", marginTop: 8, flexWrap: "wrap" }}>
            <span><b style={{ color: "#0e9f6e" }}>■</b> Blessed · {summary.library.blessed}</span>
            <span><b style={{ color: "#c4cbdb" }}>■</b> Not blessed · {summary.library.not_blessed}</span>
            <span className="note" style={{ marginLeft: "auto" }}>Blessed means admissible; selectivity lives in the test funnels above.</span>
          </div>
        </div>
      )}

      {(() => {
        const cur = picked;
        return (
          <div style={{ position: "sticky", top: 8, zIndex: 30, display: "flex", alignItems: "center", gap: 14, background: "var(--ink, #191f2c)", color: "#fff", borderRadius: 10, padding: "14px 18px", margin: "30px 0 4px", boxShadow: "0 8px 22px rgba(25,31,44,0.18)" }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#b6becd" }}>Now investigating</div>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.15rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cur ? (cur.label ?? cur.feature) : summary ? "No feature matches — adjust the filter in Browse" : "Loading feature library…"}</div>
              {cur && <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.76rem", color: "#c4cbdb", marginTop: 2 }}>{cur.kind} · best target {cur.best_target ?? "—"}</div>}
            </div>
            {cur && (
              <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "0.74rem", borderRadius: 7, padding: "6px 12px", whiteSpace: "nowrap", background: cur.blessed ? "#1f7a57" : "#586074", color: "#fff" }}>
                {cur.blessed ? "◆ Blessed" : "○ Not blessed"}
              </span>
            )}
          </div>
        );
      })()}
      {pickNote && <div className="banner show" style={{ display: "flex" }}><span>ⓘ</span><span>{pickNote}</span></div>}

      <div className="card">
        <div className="h3"><span>Investigate <span className="note">feature vs forward target · univariate</span></span></div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ maxWidth: "100%" }}>
            <label className="note" style={{ display: "block", marginBottom: 4 }}>Feature</label>
            <select value={featureKey} onChange={(e) => setFeatureKey(e.target.value)} style={{ minWidth: "min(220px, 100%)", maxWidth: "100%" }}>
              {(candidates ?? []).map((c) => (
                <option key={c.feature_key} value={c.feature_key}>{c.label ?? c.feature_key} {c.verdict ? `(${c.verdict})` : "(not yet investigated)"}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="note" style={{ display: "block", marginBottom: 4 }}>Against target</label>
            <select
              value={targetType === "segment" ? `seg:${segmentKey}` : targetType}
              onChange={(e) => {
                const v = e.target.value;
                if (v.startsWith("seg:")) {
                  setTargetType("segment");
                  setSegmentKey(v.slice(4));
                } else {
                  setTargetType(v as "instrument" | "stocks_pooled");
                }
              }}
            >
              <option value="stocks_pooled">Universal — all stocks (pooled) · return</option>
              <optgroup label="Cap tier">
                {segments.filter((x) => x.dimension === "cap").map((x) => (
                  <option key={x.segment_key} value={`seg:${x.segment_key}`}>{x.label ?? x.segment_key}</option>
                ))}
              </optgroup>
              <optgroup label="Liquidity tier">
                {segments.filter((x) => x.dimension === "liquidity").map((x) => (
                  <option key={x.segment_key} value={`seg:${x.segment_key}`}>{x.label ?? x.segment_key}</option>
                ))}
              </optgroup>
              <optgroup label="Sector">
                {segments.filter((x) => x.dimension === "sector").map((x) => (
                  <option key={x.segment_key} value={`seg:${x.segment_key}`}>{x.label ?? x.segment_key}</option>
                ))}
              </optgroup>
              <option value="instrument">Single instrument · return</option>
            </select>
          </div>
          {targetType === "instrument" && (
            <div>
              <label className="note" style={{ display: "block", marginBottom: 4 }}>Security ID</label>
              <input className="txt" value={securityId} onChange={(e) => setSecurityId(e.target.value)} placeholder="e.g. IDX:NIFTY or an ISIN" style={{ minWidth: 200 }} />
            </div>
          )}
          <div>
            <label className="note" style={{ display: "block", marginBottom: 4 }}>Horizon (days)</label>
            <input className="txt" type="number" min={1} value={horizon} onChange={(e) => setHorizon(e.target.value)} style={{ width: 80 }} />
          </div>
          <div>
            <label className="note" style={{ display: "block", marginBottom: 4 }}>Window</label>
            <select value={window_} onChange={(e) => setWindow(e.target.value as (typeof WINDOWS)[number])}>
              {WINDOWS.map((w) => <option key={w} value={w}>{w === "full" ? "full history" : `last ${w}`}</option>)}
            </select>
          </div>
          <button className="btn primary" disabled={!canRun || loading} onClick={runInvestigation}>
            {loading ? "Running…" : "Run investigation"}
          </button>
        </div>
        {selectedCandidate?.blessed_at && (
          <div className="note" style={{ marginTop: 10 }}>
            Currently in the library: {selectedCandidate.verdict}, IC {selectedCandidate.ic?.toFixed(4) ?? "—"}, last blessed {selectedCandidate.blessed_at.slice(0, 10)}.
          </div>
        )}
      </div>

      {error && <div className="error" style={{ margin: 14 }}>{error}</div>}

      <div className="card">
        <div className="h3"><span>Used by these models <span className="note">fitted models whose saved feature list includes this feature · read from the saved model files, the same source as Model Detail</span></span></div>
        {usageError && <div className="note">Could not read model usage: {usageError}</div>}
        {!usage && !usageError && <div className="note">Reading saved models…</div>}
        {usage && usage.models.length === 0 && (
          <div className="note">Not used by any fitted model. ({usage.checked} fitted models checked{usage.unreadable ? `, ${usage.unreadable} unreadable` : ""}.)</div>
        )}
        {usage && usage.models.length > 0 && (
          <table className="stocks-table">
            <thead><tr><th className="l">Model</th><th>Segment · horizon</th><th>Champion</th><th>Rank in model</th><th>Features</th></tr></thead>
            <tbody>
              {usage.models.map((m) => (
                <tr key={`${m.model_id}:${m.version}`} className="clickable" style={{ cursor: "pointer" }} onClick={() => onSelectModel(m.model_id, m.version)}>
                  <td className="l mono" style={{ fontSize: "0.78rem" }}>{m.model_id}</td>
                  <td>{m.target_ref}{m.horizon !== null ? ` · ${m.horizon}d` : ""}</td>
                  <td>{m.is_champion ? <span className="chip st-champion">champion</span> : <span className="note">challenger</span>}</td>
                  <td className="mono">{m.rank !== null ? `#${m.rank}` : "—"}</td>
                  <td className="mono">{m.n_features}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {usage && usage.models.length > 0 && <div className="foot-note">Click a row to open that model's detail. {usage.source}.</div>}
      </div>
      {result && (
        <>
          <div className="card">
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <span><VerdictBadge verdict={result.verdict} /> <FieldHelp term="bless_marginal_reject" /></span>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>{result.feature_key} — {result.target_label}</div>
                <div className="note" style={{ marginTop: 4 }}>{result.reasons.join(" · ")}</div>
              </div>
              <button className="btn primary" disabled={result.verdict === "REJECT" || blessing} onClick={doBless}
                      title={result.verdict === "REJECT" ? "Cannot bless — fails admissibility, enforced server-side" : undefined}>
                {blessing ? "Blessing…" : "✦ Bless → library"}
              </button>
            </div>
            {blessMsg && <div className="note" style={{ marginTop: 10 }}>{blessMsg}</div>}
          </div>

          <div className="sechead" style={{ fontFamily: "var(--font-display)", fontWeight: 800, margin: "20px 0 10px" }}>
            EDA <span className="note">know your data</span>
          </div>
          <div className="card">
            <div className="h3"><span>Summary statistics</span></div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8 }}>
              {[
                ["n", result.eda.n], ["mean", result.eda.mean.toFixed(3)], ["median", result.eda.median.toFixed(3)],
                ["std dev", result.eda.std.toFixed(3)], ["skew", result.eda.skew.toFixed(2)], ["excess kurt", result.eda.excess_kurtosis.toFixed(2)],
                ["min", result.eda.min.toFixed(2)], ["max", result.eda.max.toFixed(2)], ["Q1", result.eda.q1.toFixed(2)],
                ["Q3", result.eda.q3.toFixed(2)], ["missing (window)", result.eda.n_missing_in_window],
              ].map(([k, v]) => (
                <div key={String(k)} style={{ background: "var(--surface-2)", border: "1px solid var(--rule)", borderRadius: 6, padding: "8px 10px" }}>
                  <div className="note" style={{ textTransform: "uppercase", fontSize: "0.62rem" }}>{k}</div>
                  <div className="mono" style={{ fontWeight: 600 }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div className="card">
              <div className="h3"><span>Distribution <span className="note">histogram</span></span></div>
              <Histogram bins={result.eda.histogram} />
            </div>
            <div className="card">
              <div className="h3"><span>Coverage <FieldHelp term="coverage" /> <span className="note">populated fraction over the window</span></span></div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                <div className="mono" style={{ fontWeight: 700, fontSize: "1.6rem" }}>{result.coverage !== null ? `${(result.coverage * 100).toFixed(1)}%` : "—"}</div>
                <div className="note">
                  measured over the requested window, not netted against this feature's own warm-up — a newly-listed
                  security shown here may read artificially low; not used to auto-reject (bar not set in this build).
                </div>
              </div>
            </div>
          </div>

          <div className="sechead" style={{ fontFamily: "var(--font-display)", fontWeight: 800, margin: "20px 0 10px" }}>
            SIGNIFICANCE <span className="note">does it carry signal?</span>
          </div>
          <div className="card">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
              <div style={{ background: "var(--surface-2)", border: "1px solid var(--rule)", borderRadius: 6, padding: "8px 10px" }}><div className="note">IC (Spearman) <FieldHelp term="ic" /></div><div className="mono" style={{ fontWeight: 700, fontSize: "1.3rem" }}>{result.ic !== null ? `${result.ic >= 0 ? "+" : ""}${result.ic.toFixed(4)}` : "—"}</div></div>
              <div style={{ background: "var(--surface-2)", border: "1px solid var(--rule)", borderRadius: 6, padding: "8px 10px" }}><div className="note">Stability <FieldHelp term="rolling_ic_stability" /></div><div style={{ fontSize: "0.85rem" }}>{result.stability ?? "—"}</div></div>
              {result.cross_sectional && (
                <>
                  <div style={{ background: "var(--surface-2)", border: "1px solid var(--rule)", borderRadius: 6, padding: "8px 10px" }}><div className="note">Cross-sectional mean IC <FieldHelp term="cross_sectional_ic" /></div><div className="mono" style={{ fontWeight: 700 }}>{result.cross_sectional.mean_ic?.toFixed(4) ?? "—"}</div></div>
                  <div style={{ background: "var(--surface-2)", border: "1px solid var(--rule)", borderRadius: 6, padding: "8px 10px" }}><div className="note">t-stat ({result.cross_sectional.n_dates} dates) <FieldHelp term="t_stat" /></div><div className="mono" style={{ fontWeight: 700 }}>{result.cross_sectional.t_stat?.toFixed(2) ?? "—"}</div></div>
                </>
              )}
            </div>
          </div>
          {result.contributors && (
            <div className="card">
              <div className="h3"><span>Who drives the IC <span className="note">each stock's share of the cross-sectional IC, averaged over {result.contributors.dates} dates</span></span></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 12, alignItems: "start" }}>
                <div><div className="note">Mean IC, all stocks</div><div className="mono" style={{ fontWeight: 700 }}>{result.cross_sectional?.mean_ic?.toFixed(4) ?? "—"}</div></div>
                <div><div className="note">Mean IC, five largest drivers removed</div><div className="mono" style={{ fontWeight: 700 }}>{result.ic_excluding_top5?.toFixed(4) ?? "—"}</div></div>
                <div className="note">If the second number stays close to the first, no handful of stocks is driving the result.</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 12 }}>
                <div>
                  <div className="note">Pushes IC up</div>
                  {result.contributors.top_positive.map((c) => (
                    <div key={c.security_id} className="mono" style={{ fontSize: "0.8rem" }}>{c.security_id} · {c.contribution.toFixed(4)}</div>
                  ))}
                </div>
                <div>
                  <div className="note">Pulls IC down</div>
                  {result.contributors.top_negative.map((c) => (
                    <div key={c.security_id} className="mono" style={{ fontSize: "0.8rem" }}>{c.security_id} · {c.contribution.toFixed(4)}</div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div className="card">
              <div className="h3"><span>Rolling IC <FieldHelp term="rolling_ic_stability" /> <span className="note">persists, or flips by regime?</span></span></div>
              <RollingIcChart values={result.evidence.rolling_ic ?? []} />
            </div>
            <div className="card">
              <div className="h3"><span>Forward return by decile <FieldHelp term="decile_monotonicity" /> <span className="note">monotonic = real</span></span></div>
              <DecileChart means={result.evidence.decile_means ?? []} />
              <div className="note" style={{ marginTop: 6 }}>
                monotonicity {result.evidence.decile_monotonicity !== null && result.evidence.decile_monotonicity !== undefined ? result.evidence.decile_monotonicity.toFixed(3) : "n/a"}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="h3"><span>Look-ahead <FieldHelp term="look_ahead" /> <span className="note">F4a.8's structural perturbation test</span></span></div>
            {result.lookahead_tested ? (
              <div className="note">Tested on the security's full history: {result.reasons.find((r) => r.toLowerCase().includes("look-ahead")) ?? "no counter-example found"}.</div>
            ) : (
              <div className="empty-state">
                <div className="headline">Not tested</div>
                <div>
                  {targetType === "stocks_pooled"
                    ? "The pooled cross-sectional target doesn't run the truncation-recompute test directly (stated scope cut) — the property is structural to the feature's definition, not the target; investigate this feature against a single instrument to exercise it."
                    : "This feature has no compute_all-visible definition (e.g. it's read from a separate table like beta_nifty_252) and isn't testable this way."}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <div className="sechead" style={{ fontFamily: "var(--font-display)", fontWeight: 800, margin: "20px 0 10px" }}>
        GROUND-TRUTH CONTROLS <span className="note">F4a.8 — the pipeline is not trusted until it passes</span>
      </div>
      <div className="card">
        <div className="table-wrap" style={{ overflowX: "auto" }}>
          <table className="stocks-table">
            <thead><tr><th className="l">Control</th><th>Required</th><th>Actual</th><th>Passed</th><th className="l">Reason</th></tr></thead>
            <tbody>
              {(controls ?? []).map((c) => (
                <tr key={c.control}>
                  <td className="l mono">{c.control}</td>
                  <td><VerdictBadge verdict={c.required_verdict} /></td>
                  <td><VerdictBadge verdict={c.actual_verdict} /></td>
                  <td>{c.passed ? <span className="chip st-champion">✓ pass</span> : <span className="chip st-challenger">✕ FAIL</span>}</td>
                  <td className="l" style={{ fontSize: "0.78rem", color: "var(--ink-soft)" }}>{c.reasons[0]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="foot-note">Run live, not just in pytest — if any control lands on the wrong verdict, the pipeline is wrong, not the control (F4a.8 / substantiation A10).</div>
      </div>

      <div className="sechead" style={{ fontFamily: "var(--font-display)", fontWeight: 800, margin: "20px 0 10px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span>BROWSE <span className="note">pick a feature to investigate · click a row to load it above</span></span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {(["all", "blessed", "not_blessed"] as const).map((f) => (
            <button key={f} className={`btn sm${libFilter === f ? " primary" : " ghost"}`} onClick={() => setLibFilter(f)}>
              {f === "all" ? `All ${summary?.library.total ?? ""}` : f === "blessed" ? `Blessed ${summary?.library.blessed ?? ""}` : `Not blessed ${summary?.library.not_blessed ?? ""}`}
            </button>
          ))}
        </span>
        <select value={libKind} onChange={(e) => setLibKind(e.target.value)}>
          <option value="all">All kinds</option>
          {[...new Set((summary?.library.rows ?? []).map((r) => r.kind))].sort().map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
        <input placeholder="Search feature…" value={libSearch} onChange={(e) => setLibSearch(e.target.value)} style={{ minWidth: 140, maxWidth: "100%" }} />
      </div>
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table className="stocks-table" style={{ minWidth: 760, width: "100%" }}>
            <thead>
              <tr>
                <th className="l">Feature</th>
                <th>Kind</th>
                <th className="l">Best target</th>
                <th className="r">IC</th>
                <th className="l">Stability</th>
                <th className="r">Coverage</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const rows = (summary?.library.rows ?? []).filter((r: ResearchLibraryRowDto) =>
                  (libFilter === "all" || (libFilter === "blessed" ? r.blessed : !r.blessed)) &&
                  (libKind === "all" || r.kind === libKind) &&
                  (!libSearch || (r.label ?? r.feature).toLowerCase().includes(libSearch.toLowerCase())));
                return rows.length === 0 ? (
                  <tr><td colSpan={7} className="note" style={{ textAlign: "center", padding: 22 }}>{summary ? "No features match." : "Loading library…"}</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.feature} className="clickable" style={{ cursor: "pointer" }} onClick={() => pickFeature(r)}>
                    <td className="l" style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>{r.label ?? r.feature}</td>
                    <td><span className="chip">{r.kind}</span></td>
                    <td className="l" style={{ fontSize: "0.78rem" }}>{r.best_target ?? "—"}</td>
                    <td className="r mono">{r.ic !== null ? `${r.ic >= 0 ? "+" : ""}${r.ic.toFixed(4)}` : "—"}</td>
                    <td className="l" style={{ fontSize: "0.78rem", maxWidth: 260 }}><span title={r.stability ?? ""} style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.stability ?? "—"}</span></td>
                    <td className="r mono">{r.coverage !== null ? `${(Number(r.coverage) * 100).toFixed(0)}%` : "—"}</td>
                    <td><span className="chip">{r.blessed ? "blessed" : "not blessed"}</span></td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
